import { Anchor, Box, Container, Divider, Grid, Group, Stack, Text } from '@mantine/core';
import { Link } from '@tanstack/react-router';

import { useWaitlistMode } from '../../lib/auth-context';
import { useCmsSlot } from '../../lib/cms';
import { useBrowseDestination } from '../../lib/marketing-nav';
import { SOCIAL_LINKS } from '../brand/SocialIcons';

/**
 * SiteFooter — three-column footer shared by AppShell (variant="app") and
 * PublicShell (variant="public").
 *
 * The shape is identical across both shells; only the palette and a couple of
 * the Product-column links differ. Legal entity per CLAUDE.md:
 * "WWAAVE PTY LTD · ACN 689 639 423". Contact: hello@wwaave.com.
 */

type FooterTo =
  | '/browse'
  | '/explore'
  | '/events'
  | '/how-it-works'
  | '/founding-venues'
  | '/sign-up'
  | '/earlyaccess'
  | '/our-ethos'
  | '/community-guidelines'
  | '/dispute-resolution'
  | '/ripples'
  | '/faq'
  | '/our-story'
  | '/privacy'
  | '/terms';

interface FooterLink {
  readonly to: FooterTo;
  readonly label: string;
}

export function SiteFooter({ variant }: { variant: 'app' | 'public' }) {
  const isApp = variant === 'app';
  // Pre-launch the account link points at the early-access page (the one home
  // for the waitlist capture); it flips to /sign-up once sign-ups open.
  const { signupsOpen, loaded } = useWaitlistMode();
  const joinTo: FooterTo = loaded && signupsOpen ? '/sign-up' : '/earlyaccess';
  // "Browse Venues" only points at /browse once browse is actually open —
  // pre-launch it lands on the waitlist capture. Same rule as the top nav; see
  // useBrowseDestination for why (feedback ZxI3VUjjCqKnNWM_yl2eh).
  const browseTo: FooterTo = useBrowseDestination();
  const linkColor = 'ink.3';
  const metaColor = 'ink.4';
  const year = new Date().getFullYear();

  // CMS-editable footer copy (admin → /admin/settings → CMS → Site footer).
  const tagline = useCmsSlot<'markdown'>('footer.tagline');
  const legalEntity = useCmsSlot<'markdown'>('footer.legal_entity');
  const contactEmail = useCmsSlot<'markdown'>('footer.contact_email');

  // CMS-editable column headings.
  const socialHeading = useCmsSlot<'markdown'>('footer.heading_social');

  // CMS-editable Acknowledgement of Country. Sensitive cultural content — see
  // the slot's helpText in cms/slots-extra/footer.ts before editing wording.
  const acknowledgement = useCmsSlot<'markdown'>('footer.acknowledgement_of_country');

  // CMS-editable link labels. Hrefs/routes stay hardcoded below; each label is
  // mapped statically to one fixed route (never index-zipped to a route list).
  const browseLabel = useCmsSlot<'markdown'>('footer.link_browse');
  const eventsLabel = useCmsSlot<'markdown'>('footer.link_events');
  const howItWorksLabel = useCmsSlot<'markdown'>('footer.link_how_it_works');
  const foundingLabel = useCmsSlot<'markdown'>('footer.link_founding');
  const signUpLabel = useCmsSlot<'markdown'>('footer.link_sign_up');
  const whatWeAreLabel = useCmsSlot<'markdown'>('footer.link_what_we_are');
  const communityGuidelinesLabel = useCmsSlot<'markdown'>('footer.link_community_guidelines');
  const disputeResolutionLabel = useCmsSlot<'markdown'>('footer.link_dispute_resolution');
  const notesLabel = useCmsSlot<'markdown'>('footer.link_notes');
  const faqLabel = useCmsSlot<'markdown'>('footer.link_faq');
  const aboutLabel = useCmsSlot<'markdown'>('footer.link_about');
  const privacyLabel = useCmsSlot<'markdown'>('footer.link_privacy');
  const termsLabel = useCmsSlot<'markdown'>('footer.link_terms');

  const productLinks: ReadonlyArray<FooterLink> = isApp
    ? [
        // The app footer keeps the real /browse: it sits inside the member
        // shell alongside the sidebar's own Browse link, and a founding-cohort
        // member can use the page today. Only the public footer redirects.
        { to: '/browse', label: browseLabel },
        { to: '/events', label: eventsLabel },
        { to: '/how-it-works', label: howItWorksLabel },
        { to: '/founding-venues', label: foundingLabel },
      ]
    : [
        { to: browseTo, label: browseLabel },
        { to: '/how-it-works', label: howItWorksLabel },
        { to: '/founding-venues', label: foundingLabel },
        { to: joinTo, label: signUpLabel },
      ];

  const trustLinks: ReadonlyArray<FooterLink> = [
    { to: '/our-ethos', label: whatWeAreLabel },
    { to: '/community-guidelines', label: communityGuidelinesLabel },
    { to: '/dispute-resolution', label: disputeResolutionLabel },
    { to: '/ripples', label: notesLabel },
    // '/recaps' link removed from the footer (feedback Yta6p81ALHJp1k_YfVugn) —
    // read as an obsolete duplicate. The /recaps page itself still exists.
    { to: '/faq', label: faqLabel },
  ];

  const legalLinks: ReadonlyArray<FooterLink> = [
    { to: '/our-story', label: aboutLabel },
    { to: '/privacy', label: privacyLabel },
    { to: '/terms', label: termsLabel },
  ];

  // The footer sits on a dark ground in BOTH schemes (ink-9 light / dark-9 dark),
  // so the column headings need a light colour in both. `c="ink.1"` failed in
  // dark mode — warmth.css flips the ink ramp there, so ink-1 became a near-black
  // charcoal on the dark footer. light-dark() keeps it near-white either way.
  const headingProps = {
    style: {
      color: 'light-dark(var(--mantine-color-ink-1), var(--mantine-color-ink-9))',
    },
  } as const;
  const boxProps = isApp ? {} : { mt: '3xl' as const };

  return (
    <Box
      component="footer"
      {...boxProps}
      style={() => ({
        borderTop: 'none',
        background: 'light-dark(var(--mantine-color-ink-9), var(--mantine-color-dark-9))',
        color: 'light-dark(var(--mantine-color-ink-1), var(--mantine-color-dark-1))',
        ...(isApp ? { marginTop: 48 } : {}),
        position: 'relative',
      })}
    >
      <Box
        style={{
          height: 3,
          background:
            'linear-gradient(90deg, var(--mantine-color-brand-6), var(--mantine-color-sun-6))',
        }}
      />
      <Box
        style={{
          background: 'light-dark(var(--mantine-color-ink-9), var(--mantine-color-dark-9))',
        }}
      >
        <Container size="lg" py="md">
          <Text size="xs" ta="center" c={metaColor} style={{ lineHeight: 1.6, maxWidth: 720, marginInline: 'auto' }}>
            {acknowledgement}
          </Text>
        </Container>
      </Box>
      <Container size="lg" py="xl">
        <Grid gap="xl">
          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            <Stack gap="sm">
              {productLinks.map((l) => (
                <Anchor key={`${l.to}|${l.label}`} component={Link} to={l.to} c={linkColor} size="sm">
                  {l.label}
                </Anchor>
              ))}
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            <Stack gap="sm">
              {trustLinks.map((l) => (
                <Anchor key={`${l.to}|${l.label}`} component={Link} to={l.to} c={linkColor} size="sm">
                  {l.label}
                </Anchor>
              ))}
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            <Stack gap="sm">
              {legalLinks.map((l) => (
                <Anchor key={`${l.to}|${l.label}`} component={Link} to={l.to} c={linkColor} size="sm">
                  {l.label}
                </Anchor>
              ))}
              <Anchor href={`mailto:${contactEmail}`} c={linkColor} size="sm">
                {contactEmail}
              </Anchor>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
            <Stack gap="sm">
              <Text fw={600} size="sm" {...headingProps}>
                {socialHeading}
              </Text>
              <Group gap="md" wrap="wrap">
                {SOCIAL_LINKS.map(({ name, href, Icon }) => (
                  <Anchor
                    key={name}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    c={linkColor}
                    aria-label={name}
                  >
                    <Icon size={20} />
                  </Anchor>
                ))}
              </Group>
            </Stack>
          </Grid.Col>
        </Grid>

        <Divider my="lg" />

        <Stack gap="xs">
          <Text size="xs" c={metaColor}>
            {isApp ? `© ${year} ${legalEntity}` : legalEntity}
          </Text>
          <Text size="xs" c={metaColor}>
            {tagline}
          </Text>
        </Stack>
      </Container>
    </Box>
  );
}
